import type { ConnectionType } from '@deskworks/shared-types';
import { Braces, List, Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** How a connection's configuration is shown and edited: one row per key, or the raw JSON. */
export type ConfigMode = 'fields' | 'json';

const MODE_KEY = 'deskworks.connection.configMode';

function readMode(): ConfigMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'json' ? 'json' : 'fields';
  } catch {
    return 'fields';
  }
}

/** The remembered mode, shared by the connection dialog and the connection page. */
export function useConfigMode(): [ConfigMode, (mode: ConfigMode) => void] {
  const [mode, setMode] = useState<ConfigMode>(readMode);
  const change = (next: ConfigMode) => {
    setMode(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // Remembering the mode is only a convenience.
    }
  };
  return [mode, change];
}

export function ConfigModeSwitch({ value, onChange }: { value: ConfigMode; onChange: (mode: ConfigMode) => void }) {
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v === 'json' ? 'json' : 'fields')}>
      <TabsList className="h-8">
        <TabsTrigger value="fields" className="px-2.5 text-xs">
          <List className="size-3.5" /> {t('Key / value')}
        </TabsTrigger>
        <TabsTrigger value="json" className="px-2.5 text-xs">
          <Braces className="size-3.5" /> JSON
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

type ValueKind = 'text' | 'number' | 'boolean' | 'list' | 'json';

interface ConfigKey {
  key: string;
  kind: ValueKind;
  hint: string;
  example?: string;
}

const AUTH_HEADER: ConfigKey = { key: 'authHeader', kind: 'text', hint: t('Header the credentials go into (default Authorization).'), example: 'Authorization' };
const AUTH_SCHEME: ConfigKey = { key: 'authScheme', kind: 'text', hint: t('Put before the token (default Bearer); empty sends the token alone.'), example: 'Bearer' };
const HEADERS: ConfigKey = { key: 'headers', kind: 'json', hint: t('Extra non-secret headers as a JSON object.'), example: '{"X-Tenant": "acme"}' };

/** The keys Deskworks reads per connection type — offered as suggestions, anything else may still be added. */
export const CONFIG_KEYS: Partial<Record<ConnectionType, ConfigKey[]>> = {
  http_api: [
    { key: 'baseUrl', kind: 'text', hint: t('Every call stays below this address.'), example: 'https://api.example.com' },
    AUTH_HEADER,
    AUTH_SCHEME,
    HEADERS,
    { key: 'healthPath', kind: 'text', hint: t('Path the health check calls.'), example: '/health' },
    { key: 'notes', kind: 'text', hint: t('Shown to agents: how to use this API.') },
  ],
  git_repository: [
    { key: 'url', kind: 'text', hint: t('The https clone URL.'), example: 'https://gitlab.com/group/app.git' },
    { key: 'username', kind: 'text', hint: t('User name sent with the token (GitLab: oauth2).'), example: 'oauth2' },
    { key: 'pushBranchPrefix', kind: 'text', hint: t('Agents may only push branches starting with this (default deskworks/).'), example: 'deskworks/' },
    { key: 'defaultBranch', kind: 'text', hint: t('The branch merge requests go into.'), example: 'main' },
  ],
  mcp_server: [
    { key: 'url', kind: 'text', hint: t('Address of the MCP server (Streamable HTTP).'), example: 'https://example.com/mcp' },
    HEADERS,
    AUTH_HEADER,
    AUTH_SCHEME,
    { key: 'adapter', kind: 'text', hint: t('Built-in adapter Deskworks serves itself.'), example: 'gitlab' },
    { key: 'baseUrl', kind: 'text', hint: t('Address of the system the adapter calls.'), example: 'https://gitlab.com' },
    { key: 'projects', kind: 'list', hint: t('Projects agents may reach; empty = all the token can see.'), example: 'group/app, group/gitops' },
    { key: 'branchPrefix', kind: 'text', hint: t('Agents only act on branches starting with this (default deskworks/).'), example: 'deskworks/' },
  ],
  email: [{ key: 'address', kind: 'text', hint: t('The mailbox address.'), example: 'team@example.com' }],
  gitlab: [
    { key: 'baseUrl', kind: 'text', hint: t('Address of your GitLab.'), example: 'https://gitlab.com' },
    { key: 'projectId', kind: 'text', hint: t('Project path or ID.'), example: 'group/app' },
  ],
  github: [
    { key: 'owner', kind: 'text', hint: t('User or organisation.'), example: 'acme' },
    { key: 'repo', kind: 'text', hint: t('Repository name.'), example: 'app' },
  ],
  slack: [{ key: 'channel', kind: 'text', hint: t('Channel to post in.'), example: '#general' }],
};

function knownKey(type: ConnectionType, key: string): ConfigKey | undefined {
  return CONFIG_KEYS[type]?.find((k) => k.key === key);
}

function kindOf(value: unknown): ValueKind {
  if (typeof value === 'string') return 'text';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) return 'list';
  return 'json';
}

function valueText(value: unknown, kind: ValueKind): string {
  if (kind === 'list') return (value as string[]).join(', ');
  if (kind === 'json') return JSON.stringify(value);
  return String(value ?? '');
}

export interface ConfigRow {
  id: string;
  key: string;
  value: string;
  kind: ValueKind;
}

let nextRowId = 0;
const newRow = (key = '', value = '', kind: ValueKind = 'text'): ConfigRow => ({ id: String(++nextRowId), key, value, kind });

export function rowsFromConfig(config: Record<string, unknown>): ConfigRow[] {
  return Object.entries(config).map(([key, value]) => {
    const kind = kindOf(value);
    return newRow(key, valueText(value, kind), kind);
  });
}

/** The config object of the rows, or the first problem in them. Rows without key and value are ignored. */
export function configFromRows(rows: ConfigRow[]): { value: Record<string, unknown> } | { error: string } {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    const key = r.key.trim();
    if (!key && !r.value.trim()) continue;
    if (!key) return { error: t('The value “{value}” has no key.', { value: r.value }) };
    if (key in out) return { error: t('“{key}” appears twice.', { key }) };
    if (r.kind === 'number') {
      const n = Number(r.value);
      if (!r.value.trim() || Number.isNaN(n)) return { error: t('“{key}” must be a number.', { key }) };
      out[key] = n;
    } else if (r.kind === 'boolean') {
      out[key] = r.value === 'true';
    } else if (r.kind === 'list') {
      out[key] = r.value.split(',').map((v) => v.trim()).filter(Boolean);
    } else if (r.kind === 'json') {
      try {
        out[key] = JSON.parse(r.value);
      } catch {
        return { error: t('“{key}” is not valid JSON.', { key }) };
      }
    } else {
      out[key] = r.value;
    }
  }
  return { value: out };
}

/** One row per config key; known keys of the type are suggested while typing and as one-click additions. */
export function ConfigFieldsEditor({ type, rows, onChange }: { type: ConnectionType; rows: ConfigRow[]; onChange: (rows: ConfigRow[]) => void }) {
  const listId = useId();
  const suggestions = CONFIG_KEYS[type] ?? [];
  const unused = suggestions.filter((s) => !rows.some((r) => r.key.trim() === s.key));

  const update = (id: string, patch: Partial<ConfigRow>) => onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const changeKey = (r: ConfigRow, key: string) => {
    const known = knownKey(type, key.trim());
    // A known key brings its kind along, as long as nothing was typed into the value yet.
    update(r.id, known && !r.value ? { key, kind: known.kind } : { key });
  };

  return (
    <div className="grid gap-2">
      <datalist id={listId}>
        {unused.map((s) => (
          <option key={s.key} value={s.key}>
            {s.hint}
          </option>
        ))}
      </datalist>
      {rows.map((r) => {
        const known = knownKey(type, r.key.trim());
        return (
          <div key={r.id} className="grid gap-1">
            <div className="flex items-center gap-2">
              <Input
                value={r.key}
                onChange={(e) => changeKey(r, e.target.value)}
                list={listId}
                placeholder={t('Key')}
                aria-label={t('Key')}
                className="w-2/5 shrink-0 font-mono text-[13px]"
                spellCheck={false}
              />
              <ValueInput row={r} example={known?.example} onChange={(value) => update(r.id, { value })} />
              <Button type="button" variant="ghost" size="icon" aria-label={t('Remove {name}', { name: r.key || t('row') })} onClick={() => onChange(rows.filter((x) => x.id !== r.id))}>
                <X />
              </Button>
            </div>
            {known && <p className="pl-0.5 text-xs text-muted-foreground">{known.hint}</p>}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-1.5">
        <Button type="button" variant="outline" size="xs" onClick={() => onChange([...rows, newRow()])}>
          <Plus /> {t('Add field')}
        </Button>
        {unused.map((s) => (
          <Button
            key={s.key}
            type="button"
            variant="ghost"
            size="xs"
            title={s.hint}
            className="font-mono text-muted-foreground"
            onClick={() => onChange([...rows, newRow(s.key, s.kind === 'boolean' ? 'false' : '', s.kind)])}
          >
            <Plus /> {s.key}
          </Button>
        ))}
      </div>
    </div>
  );
}

function ValueInput({ row, example, onChange }: { row: ConfigRow; example?: string; onChange: (value: string) => void }) {
  if (row.kind === 'boolean') {
    return (
      <div className="flex h-9 flex-1 items-center gap-2 text-sm">
        <Switch checked={row.value === 'true'} onCheckedChange={(on) => onChange(on ? 'true' : 'false')} aria-label={row.key} />
        <span className="text-muted-foreground">{row.value === 'true' ? t('on') : t('off')}</span>
      </div>
    );
  }
  const placeholder = row.kind === 'list' ? (example ?? 'a, b, c') : row.kind === 'json' ? (example ?? '{ … }') : (example ?? t('Value'));
  return (
    <Input
      value={row.value}
      onChange={(e) => onChange(e.target.value)}
      type={row.kind === 'number' ? 'number' : 'text'}
      placeholder={placeholder}
      aria-label={t('Value of {name}', { name: row.key || t('new field') })}
      title={row.kind === 'list' ? t('Comma-separated') : row.kind === 'json' ? 'JSON' : undefined}
      className={cn('min-w-0 flex-1 font-mono text-[13px]', row.kind === 'json' && 'text-xs')}
      spellCheck={false}
    />
  );
}

/** Read-only view of a configuration, switchable between key/value and JSON. */
export function ConfigView({ type, config }: { type: ConnectionType; config: Record<string, unknown> }) {
  const [mode, setMode] = useConfigMode();
  return (
    <div className="grid gap-3">
      <ConfigModeSwitch value={mode} onChange={setMode} />
      {mode === 'json' ? (
        <JsonBlock value={config} />
      ) : (
        <dl className="grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-2.5 text-sm">
          {Object.entries(config).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="font-mono text-[13px] text-muted-foreground" title={knownKey(type, key)?.hint}>
                {key}
              </dt>
              <dd className="min-w-0 break-words">
                <ConfigValue value={value} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function ConfigValue({ value }: { value: unknown }) {
  const kind = kindOf(value);
  if (kind === 'json') return <JsonBlock value={value} maxHeight="max-h-48" />;
  if (kind === 'boolean') return <span>{value ? t('on') : t('off')}</span>;
  if (kind === 'list') {
    const list = value as string[];
    return list.length ? <span className="font-mono text-[13px]">{list.join(', ')}</span> : <span className="text-muted-foreground">—</span>;
  }
  const text = String(value);
  return text ? <span className="font-mono text-[13px]">{text}</span> : <span className="text-muted-foreground">{t('(empty)')}</span>;
}
